import { useCallback, useEffect, useMemo, useRef } from 'react';

import useGrammarCheck from './useGrammarCheck';
import { CheckableElement } from '../checkable-elements/CheckableElement';
import { ContentEditableCheckableElement } from '../checkable-elements/ContentEditableCheckableElement';
import { TextareaCheckableElement } from '../checkable-elements/TextareaCheckableElement';
import { GrammarCheckAlert } from '../types';

export type ElementType = 'textarea' | 'contenteditable';

function createCheckableElement(
  element: HTMLElement,
  type: ElementType,
): CheckableElement {
  switch (type) {
    case 'textarea':
      return new TextareaCheckableElement(element);
    case 'contenteditable':
      return new ContentEditableCheckableElement(element);
  }
}

/**
 * High-level hook that combines grammar checking logic with element binding.
 * Works with controlled text state — receives `text` value and `onTextChange`
 * callback instead of attaching native DOM event listeners.
 * The `element` ref is still required for visual highlight positioning.
 */
const useGrammarHighlighter = (
  element: HTMLElement | null,
  elementType: ElementType,
  isEnabled: boolean,
  text: string,
  onTextChange?: (text: string) => void,
  onNewAlerts?: (alerts: GrammarCheckAlert[]) => void,
) => {
  const {
    alerts,
    stats,
    alertApplied,
    alertDismissed,
    textChanged,
    isLoadingGrammarCheck,
    clearAlerts,
  } = useGrammarCheck(isEnabled, onNewAlerts);

  const checkableElementRef = useRef<CheckableElement | null>(null);

  const checkableElement = useMemo(() => {
    if (checkableElementRef.current) {
      checkableElementRef.current.destroy();
    }

    if (!element) {
      checkableElementRef.current = null;
      return null;
    }

    const ce = createCheckableElement(element, elementType);
    checkableElementRef.current = ce;
    return ce;
  }, [element, elementType]);

  useEffect(() => {
    return () => {
      checkableElementRef.current?.destroy();
    };
  }, []);

  const textChangedRef = useRef(textChanged);
  useEffect(() => {
    textChangedRef.current = textChanged;
  }, [textChanged]);

  useEffect(() => {
    if (!isEnabled) return;
    textChangedRef.current(text);
  }, [text, isEnabled]);

  const handleAlertInsert = useCallback(
    (alert: GrammarCheckAlert, _replacement: string) => {
      if (!onTextChange) return;
      const { absOffset, length } = alert;
      const before = text.substring(0, absOffset);
      const after = text.substring(absOffset + length);
      onTextChange(before + alert.replacements[0] + after);
      alertApplied(alert);
    },
    [text, onTextChange, alertApplied],
  );

  const handleAlertDismiss = useCallback(
    (alert: GrammarCheckAlert) => {
      alertDismissed(alert);
    },
    [alertDismissed],
  );

  const handleApplyAll = useCallback(async () => {
    if (!onTextChange || alerts.length === 0) return;
    let result = text;
    let offsetShift = 0;
    for (const alert of alerts) {
      const adjustedOffset = alert.absOffset + offsetShift;
      const before = result.substring(0, adjustedOffset);
      const after = result.substring(adjustedOffset + alert.length);
      result = before + alert.replacements[0] + after;
      offsetShift += alert.replacements[0].length - alert.length;
    }
    onTextChange(result);
    clearAlerts();
  }, [text, alerts, onTextChange, clearAlerts]);

  const HighlighterComponent = checkableElement?.getHighlighter() ?? null;

  return {
    alerts,
    stats,
    isLoading: isLoadingGrammarCheck,
    checkableElement,
    HighlighterComponent,

    textChanged,
    alertApplied,
    alertDismissed,
    clearAlerts,

    handleAlertInsert,
    handleAlertDismiss,
    handleApplyAll,
  };
};

export default useGrammarHighlighter;
